/* eslint-disable react-refresh/only-export-components */
import { DocumentV2, videoTypeRegex } from '@eigtech/documents-types'
import { DataGrid, DataGridProps, Table } from '@eigtech/ui-shared-data-grid'
import { Image, Skeleton, Stack, chakra } from '@eigtech/ui-shared-dave'
import {
  DocumentV2WithTags,
  useGetDocumentUrl,
  useOrganizeMedia,
} from '@eigtech/ui-shared-documents'
import { useDisclosure } from '@eigtech/ui/hooks/use-disclosure'
import {
  FC,
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react'
import {
  DocumentColumn,
  documentColumnHelper,
  hiddenDocumentColumns,
  useSharedDocumentsColumns,
} from './DocumentsTableColumns'
import { MediaCarouselProps } from './MediaCarousel/MediaCarousel'
import { MediaCarouselModal } from './MediaCarousel/MediaCarouselModal'

export type MediaTableProps = {
  media: DocumentV2WithTags[] | undefined
  enableBulkActions: boolean
  ActionMenu?: FC<{ document: DocumentColumn }>
  hideColumns?: Partial<Record<keyof ReturnType<typeof useSharedDocumentsColumns>, boolean>>
} & Omit<DataGridProps<DocumentColumn>, 'data' | 'columns'>

export const MediaTable = forwardRef(function MediaTable(
  {
    media,
    ActionMenu,
    enableBulkActions,
    hideColumns,
    enableSubRowSelection = false,
    ...tableProps
  }: MediaTableProps,
  ref: ForwardedRef<Table<DocumentColumn>>
) {
  const tableRef = useRef<Table<DocumentColumn>>(null!)
  useImperativeHandle(ref, () => ({
    ...tableRef.current,
  }))

  const groupedMedia = useOrganizeMedia(media)

  const columns = useMediaColumns({
    ActionMenu,
    enableBulkActions,
    hideColumns,
  })

  return (
    <Stack>
      <DataGrid<DocumentColumn>
        ref={tableRef}
        enableSubRowSelection={enableSubRowSelection}
        {...tableProps}
        columns={columns}
        data={groupedMedia}
        getSubRows={(row) => row.subRows}
        initialState={hiddenDocumentColumns}
        state={tableProps.state}
      />
    </Stack>
  )
})

export function useMediaColumns({
  ActionMenu,
  enableBulkActions,
  hideColumns,
}: Pick<MediaTableProps, 'enableBulkActions' | 'hideColumns'> & {
  ActionMenu?: FC<{ document: DocumentColumn }>
}) {
  const sharedColumns = useSharedDocumentsColumns()

  return useMemo(
    () => [
      ...(enableBulkActions ? [sharedColumns.actionMenu({ ActionMenu })] : []),
      ...(enableBulkActions && !hideColumns?.importStatus ? [sharedColumns.importStatus] : []),
      documentColumnHelper.display({
        id: 'download',
        header: '',
        maxSize: 80,
        enableSorting: false,
        enableColumnFilter: false,
        enableHiding: false,
        cell: (cell) => (
          <Media
            getDocuments={() =>
              cell.table.getPrePaginationRowModel().rows.map((row) => row.original)
            }
            {...cell.row.original}
          />
        ),
      }),
      ...(!hideColumns?.title ? [sharedColumns.title] : []),
      ...(!hideColumns?.description ? [sharedColumns.description] : []),
      ...(!hideColumns?.filename ? [sharedColumns.filename] : []),
      ...(!hideColumns?.createdBy ? [sharedColumns.createdBy] : []),
      ...(!hideColumns?.visibility ? [sharedColumns.visibility] : []),
      ...(!hideColumns?.exportHistory ? [sharedColumns.exportHistory] : []),
      ...(!hideColumns?.createdDate ? [sharedColumns.createdDate] : []),
      ...(!hideColumns?.modifiedDate ? [sharedColumns.modifiedDate] : []),
      ...(!hideColumns?.type ? [sharedColumns.type] : []),
      ...(!hideColumns?.source ? [sharedColumns.source] : []),
      ...(!hideColumns?.dates ? [sharedColumns.dates] : []),
      ...(!hideColumns?.meta ? [sharedColumns.meta] : []),
      ...(!hideColumns?.tags && sharedColumns.tags ? [sharedColumns.tags] : []),
    ],
    [ActionMenu, enableBulkActions, hideColumns, sharedColumns]
  )
}

function Media({
  getDocuments,
  ...document
}: DocumentV2 & Pick<MediaCarouselProps, 'getDocuments'>) {
  const { id, entityId, filename } = document

  const { data: url, refetch } = useGetDocumentUrl({ entityId, documentId: id })

  useEffect(() => {
    refetch()
  }, [refetch])

  const { onOpen, setOpen, open } = useDisclosure()

  return (
    <>
      {filename.match(videoTypeRegex) ? (
        <chakra.video autoPlay muted src={url} w="full" />
      ) : (
        <chakra.button onClick={onOpen}>
          <Image fallback={<Skeleton h="20" w="20" />} minW="20" src={url} />
        </chakra.button>
      )}

      {open && (
        <MediaCarouselModal
          getDocuments={getDocuments}
          initialDocument={document}
          open={open}
          onOpenChange={setOpen}
        />
      )}
    </>
  )
}
